import React, { useEffect, useState } from 'react';
import { getCsrfToken } from '../utils/auth';
import { updateDevkit } from '../utils/updateDevkit';

const channelMap = {
    Stable: 'release',
    Nightly: 'testing',
    Experimental: 'sandbox',
};

const buttonVariants = {
    primary: 'bg-[#036AA9] hover:bg-[#005f95] text-white', // Brand blue
    dark: 'bg-[#003759] hover:bg-[#002c47] text-white', // Deep blue
    navy: 'bg-[#0E103D] hover:bg-[#05061d] text-white', // Navy
    neutral: 'bg-[#57585B] hover:bg-[#3f3f41] text-white', // Slate gray
    light: 'bg-[#EBEBEB] hover:bg-[#d5d5d5] text-black', // Light gray
    ghost: 'bg-transparent hover:bg-[#036AA9]/10 text-[#036AA9]', // Transparent brand blue
};

const appConfig = {
    proteuscore: {
        label: 'ProteusCore',
        variant: 'primary',
        activeColor: 'bg-[#036AA9]/10 border-[#036AA9]/30 bg-blue-50 border-blue-100',
        hoverColor: 'hover:border-[#036AA9]/20 hover:bg-[#036AA9]/5'
    },
    ironman: {
        label: 'Configuration App',
        variant: 'dark',
        activeColor: 'bg-[#003759]/10 border-[#003759]/30',
        hoverColor: 'hover:border-[#003759]/20 hover:bg-[#003759]/5'
    },
    poseidon: {
        label: 'Poseidon',
        variant: 'neutral',
        activeColor: 'bg-[#57585B]/10 border-[#57585B]/30',
        hoverColor: 'hover:border-[#57585B]/20 hover:bg-[#57585B]/5'
    }
};

const BatchUpdateSidebar = ({
    devkits = [],
    admin = false,
    isOpen = false,
    onClose = () => {},
}) => {
    // Internal device selection state
    const [selectedDevkits, setSelectedDevkits] = useState([]);
    const count = selectedDevkits.length;

    const [activeApp, setActiveApp] = useState(null);
    const [channel, setChannel] = useState('Stable');
    const [selectedVersion, setSelectedVersion] = useState('latest');
    const [availableUpdates, setAvailableUpdates] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [currentUpdatingDevice, setCurrentUpdatingDevice] = useState(null);
    const [activeUpdateStep, setActiveUpdateStep] = useState('');
    const [updatedDevices, setUpdatedDevices] = useState([]);
    const [failedDevices, setFailedDevices] = useState([]);
    const [showResults, setShowResults] = useState(false);

    // Filter to only show online devkits
    const onlineDevkits = devkits.filter(devkit => devkit.tailscale?.ipv4?.[0]);

    const isSelected = (devkit) =>
        selectedDevkits.some((d) => d.devkit_id === devkit.devkit_id);

    const toggleDevkitSelection = (devkit) => {
        setSelectedDevkits(prev => {
            const isCurrentlySelected = prev.some(d => d.devkit_id === devkit.devkit_id);
            if (isCurrentlySelected) {
                return prev.filter(d => d.devkit_id !== devkit.devkit_id);
            } else {
                return [...prev, devkit];
            }
        });
    };

    const selectAllOnline = () => {
        setSelectedDevkits([...onlineDevkits]);
    };

    const clearSelection = () => {
        setSelectedDevkits([]);
    };

    const fetchAvailableVersions = async (app, chan) => {
        setAvailableUpdates([]);

        const res = await fetch('/api/available-versions?channel=' + channelMap[chan] + '&app=' + app, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'X-CSRF-TOKEN': getCsrfToken()
            }
        });
        if (!res.ok) return;
        const data = await res.json();

        const parseVersions = data.available_versions
            .map((v) => {
                const match = v.match(/_(\d+\.\d+\.\d+)_([\d]+)_?/);
                return match ? `${match[1]} (${match[2]})` : null;
            })
            .filter(Boolean);

        setAvailableUpdates(parseVersions);
    };

    const handleAppClick = (app) => {
        if (!activeApp) {
            setActiveApp(app);
            setChannel('Stable');
            setSelectedVersion('latest');
            fetchAvailableVersions(app, 'Stable');
        } else if (activeApp !== app) {
            setActiveApp(app);
        } else {
            setActiveApp(null);
        }
    };

    const sendBatchUpdate = async (app) => {
        setIsSending(true);
        setUpdatedDevices([]);
        setFailedDevices([]);
        setShowResults(false);

        const [version, build] =
            selectedVersion === 'latest'
            ? ['latest', undefined]
            : selectedVersion.match(/^([\d.]+) \((\d+)\)$/).slice(1);

        const updateBody = build ? { version, build: Number(build) } : { version };

        for (const devkit of selectedDevkits) {
            const name = devkit.devkit_id;
            const host = devkit.tailscale?.ipv4?.[0];
            if (!host) {
                setFailedDevices(prev => [...prev, { name, reason: 'No IP available' }]);
                continue;
            }
            setCurrentUpdatingDevice(name);
            setActiveUpdateStep('Starting update...');
            try {
                await updateDevkit({
                    host,
                    app,
                    devkitName: name,
                    updateBody,
                    onStep: (step) => setActiveUpdateStep(step || ''),
                });
                setUpdatedDevices(prev => [...prev, name]);
            } catch (err) {
                setFailedDevices(prev => [...prev, { name, reason: err?.message || 'Unknown error' }]);
            }
        }

        setIsSending(false);
        setActiveApp(null);
        setShowResults(true);
    };

    const overallStatus = (() => {
        if (failedDevices.length === 0 && updatedDevices.length > 0) return 'success';
        if (failedDevices.length > 0 && updatedDevices.length > 0) return 'partial';
        if (failedDevices.length > 0 && updatedDevices.length === 0) return 'failure';
        return 'idle';
    })();

    // Close sidebar when escape key is pressed
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Reset selection when sidebar closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedDevkits([]);
            setActiveApp(null);
            setUpdatedDevices([]);
            setFailedDevices([]);
            setShowResults(false);
        }
    }, [isOpen]);

    return (
        <>
            {/* Backdrop overlay */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <div className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-40 ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className={`w-2.5 h-2.5 rounded-full transition-colors ${
                                    count > 0 ? 'bg-emerald-500' : 'bg-gray-300'
                                }`}></div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900">Batch Update</h1>
                                    <p className="text-sm text-gray-500">
                                        {count > 0 
                                            ? `${count} of ${onlineDevkits.length} devices selected` 
                                            : `${onlineDevkits.length} devices available`
                                        }
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Scrollable content */}
                    <div className="flex-1 overflow-y-auto">
                        {/* Device Selection */}
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-sm font-medium text-gray-900">Select Devices</h2>
                                <div className="flex gap-2">
                                    <button
                                        onClick={selectAllOnline}
                                        disabled={onlineDevkits.length === 0 || onlineDevkits.length === count}
                                        className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Select All
                                    </button>
                                    <button
                                        onClick={clearSelection}
                                        disabled={count === 0}
                                        className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            
                            {onlineDevkits.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="w-12 h-12 mx-auto mb-4 text-gray-300">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1}>
                                            <circle cx="12" cy="12" r="3"/>
                                            <circle cx="12" cy="1" r="1"/>
                                            <circle cx="12" cy="23" r="1"/>
                                            <circle cx="20.49" cy="12" r="1"/>
                                            <circle cx="3.51" cy="12" r="1"/>
                                            <circle cx="18.36" cy="5.64" r="1"/>
                                            <circle cx="5.64" cy="18.36" r="1"/>
                                            <circle cx="18.36" cy="18.36" r="1"/>
                                            <circle cx="5.64" cy="5.64" r="1"/>
                                        </svg>
                                    </div>
                                    <p className="text-sm text-gray-500 font-medium">No online devices available</p>
                                    <p className="text-xs text-gray-400 mt-1">Devices must be online to appear here</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    {onlineDevkits.map((devkit) => (
                                        <div
                                            key={devkit.devkit_id}
                                            onClick={() => toggleDevkitSelection(devkit)}
                                            className={`relative p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${
                                                isSelected(devkit)
                                                ? 'bg-blue-50 border-blue-200 shadow-sm ring-1 ring-blue-200'
                                                : 'bg-white border-gray-200 hover:border-gray-300'
                                            }`}
                                            >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 min-w-0">
                                                <div
                                                    className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center transition-all ${
                                                    isSelected(devkit)
                                                        ? 'bg-blue-600 border-blue-600'
                                                        : 'border-gray-300 bg-white'
                                                    }`}
                                                >
                                                    {isSelected(devkit) && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        viewBox="0 0 16 16"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                                                    </svg>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></div>
                                                    <span className="font-mono text-sm font-medium text-gray-900 truncate">
                                                    {devkit.devkit_id}
                                                    </span>
                                                </div>
                                                </div>

                                                {/* Right side: versions */}
                                                <div className="text-[11px] text-gray-600 text-right leading-tight shrink-0">
                                                <div>
                                                    <span className="font-medium">ProteusCore:</span>{' '}
                                                    {devkit.versions?.proteuscore?.proteuscore_version || '—'}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Config App:</span>{' '}
                                                    {devkit.versions?.ironman?.ironman_version || '—'}
                                                </div>
                                                {admin && (
                                                    <div>
                                                    <span className="font-medium">Poseidon:</span>{' '}
                                                    {devkit.versions?.poseidon?.poseidon_version || '—'}
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Update Progress */}
                        {(isSending || updatedDevices.length > 0 || failedDevices.length > 0) && (
                            <div className="px-6 pb-6">
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <h3 className="text-sm font-medium text-gray-900">Update Progress</h3>
                                    
                                    {isSending && currentUpdatingDevice && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-amber-600 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                                    <circle cx="12" cy="12" r="10" className="opacity-25" />
                                                    <path d="M4 12a8 8 0 018-8" className="opacity-75" />
                                                </svg>
                                                <span className="text-sm font-medium text-amber-800">
                                                    Updating {currentUpdatingDevice}
                                                </span>
                                            </div>
                                            <p className="text-xs text-amber-600 mt-1">{activeUpdateStep || 'Preparing...'}</p>
                                        </div>
                                    )}

                                    {updatedDevices.map((deviceName) => (
                                        <div key={deviceName} className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm font-medium text-emerald-800">{deviceName}</span>
                                            </div>
                                        </div>
                                    ))}

                                    {failedDevices.map(({ name, reason }) => (
                                        <div key={`fail-${name}`} className="bg-red-50 border border-red-200 rounded-md p-3">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <div className="flex-1">
                                                    <span className="text-sm font-medium text-red-800">{name}</span>
                                                    <p className="text-xs text-red-600">{reason}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Available Updates */}
                        <div className="px-6 pb-6">
                            <h2 className="text-sm font-medium text-gray-900 mb-4">Available Updates</h2>
                            
                            {onlineDevkits.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="w-12 h-12 mx-auto mb-4 text-gray-300">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1}>
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                            <circle cx="9" cy="7" r="4"/>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                        </svg>
                                    </div>
                                    <p className="text-sm text-gray-500">No devices available for updates</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {['proteuscore', 'ironman', ...(admin ? ['poseidon'] : [])].map((app) => {
                                        const config = appConfig[app];
                                        const isActive = activeApp === app;
                                        
                                        return (
                                            <div key={app} className="space-y-3">
                                                <button
                                                    disabled={count === 0 || isSending}
                                                    onClick={() => handleAppClick(app)}
                                                    className={`w-full text-left p-4 rounded-lg border transition-all bg-blue-50 border-blue-100 disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h3 className="font-medium text-gray-900">{config.label}</h3>
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {count === 0 ? 'Select devices to configure' : `Ready to update ${count} device${count > 1 ? 's' : ''}`}
                                                            </p>
                                                        </div>
                                                        <svg className={`w-5 h-5 text-gray-400 transition-transform ${isActive ? 'rotate-90' : ''}`} 
                                                             viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                </button>

                                                {isActive && (
                                                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-700 mb-1">Channel</label>
                                                                <select
                                                                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                    value={channel}
                                                                    onChange={(e) => {
                                                                        setChannel(e.target.value);
                                                                        fetchAvailableVersions(app, e.target.value);
                                                                    }}
                                                                >
                                                                    {['Stable', 'Nightly', 'Experimental'].map((opt) => (
                                                                        <option key={opt} value={opt}>{opt}</option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-700 mb-1">Version</label>
                                                                <select
                                                                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                    value={selectedVersion}
                                                                    onChange={(e) => setSelectedVersion(e.target.value)}
                                                                >
                                                                    <option value="latest">Latest</option>
                                                                    {availableUpdates.map((v, i) => (
                                                                        <option key={i} value={v}>{v}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>

                                                        <div className={`border rounded-md p-3 ${config.activeColor}`}>
                                                            <div className="text-xs font-medium mb-1" style={{
                                                                color: config.variant === 'primary' ? '#036AA9' : 
                                                                       config.variant === 'dark' ? '#003759' : '#57585B'
                                                            }}>Selected Devices:</div>
                                                            <div className="text-xs" style={{
                                                                color: config.variant === 'primary' ? '#005f95' : 
                                                                       config.variant === 'dark' ? '#002c47' : '#3f3f41'
                                                            }}>
                                                                {selectedDevkits.map(d => d.devkit_id).join(', ') || 'None selected'}
                                                            </div>
                                                        </div>

                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => sendBatchUpdate(app)}
                                                                disabled={count === 0 || isSending}
                                                                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${buttonVariants[config.variant]}`}
                                                            >
                                                                {isSending ? (
                                                                    <div className="flex items-center justify-center gap-2">
                                                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                                                            <circle cx="12" cy="12" r="10" className="opacity-25" />
                                                                            <path d="M4 12a8 8 0 018-8" className="opacity-75" />
                                                                        </svg>
                                                                        Updating...
                                                                    </div>
                                                                ) : (
                                                                    `Deploy to ${count} Device${count !== 1 ? 's' : ''}`
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => setActiveApp(null)}
                                                                className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Results Modal */}
            {showResults && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-lg mx-4 rounded-xl shadow-2xl overflow-hidden">
                        <div className="p-6">
                            <div className="flex flex-col items-center text-center space-y-4">
                                {overallStatus === 'success' && (
                                    <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                                {overallStatus === 'partial' && (
                                    <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                                {overallStatus === 'failure' && (
                                    <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                                
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {overallStatus === 'success' && 'Update Complete'}
                                        {overallStatus === 'partial' && 'Partial Success'}
                                        {overallStatus === 'failure' && 'Update Failed'}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {updatedDevices.length} succeeded, {failedDevices.length} failed
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mt-6">
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                    <div className="text-sm font-medium text-emerald-800 mb-2">Succeeded</div>
                                    {updatedDevices.length === 0 ? (
                                        <div className="text-sm text-emerald-600">None</div>
                                    ) : (
                                        <div className="space-y-1 max-h-24 overflow-y-auto">
                                            {updatedDevices.map((name) => (
                                                <div key={`ok-${name}`} className="text-sm font-mono text-emerald-800">{name}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <div className="text-sm font-medium text-red-800 mb-2">Failed</div>
                                    {failedDevices.length === 0 ? (
                                        <div className="text-sm text-red-600">None</div>
                                    ) : (
                                        <div className="space-y-1 max-h-24 overflow-y-auto">
                                            {failedDevices.map(({ name, reason }) => (
                                                <div key={`fail-${name}`} className="text-sm">
                                                    <div className="font-mono text-red-800">{name}</div>
                                                    <div className="text-xs text-red-600">{reason}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowResults(false);
                                        setUpdatedDevices([]);
                                        setFailedDevices([]);
                                    }}
                                    className="flex-1 px-4 py-2 rounded-md bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        setShowResults(false);
                                        setUpdatedDevices([]);
                                        setFailedDevices([]);
                                        onClose();
                                    }}
                                    className="flex-1 px-4 py-2 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BatchUpdateSidebar;